import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AiBadge } from '@/components/ai/AiBadge';
import { runAi } from '@/lib/ai';
import { MILLS, baselineHours, energyPlanDemo, evaluatePlan } from '@/lib/energyPlan';
import { oteTypicalDay, tariffBand } from '@/data/oteData';
import { cn } from '@/lib/utils';
import { t, getLanguage, getLocale } from '@/i18n';
import { Loader2, Sparkles } from 'lucide-react';
import { HelpTip } from '@/components/ui/help-tip';

const PRICES = oteTypicalDay.hourly.map(h => h.price);
const BAND = { negative: 'bg-emerald-500', offpeak: 'bg-emerald-300', standard: 'bg-amber-300', peak: 'bg-red-400' };
const czk = (n) => `${Math.round(n).toLocaleString(getLocale())} ${t('stats.urgency.czk')}`;

// 24 cells for one mill: price band as colour, planned hours filled, today's habit outlined.
const DayStrip = ({ hours, habit }) => (
  <div className="grid grid-cols-[repeat(24,minmax(0,1fr))] gap-px">
    {PRICES.map((p, h) => (
      <div key={h} title={`${h}:00 · ${p} Kč/MWh`}
        className={cn('h-4 rounded-[2px]', hours.includes(h) ? BAND[tariffBand(p)] : 'bg-muted', habit.includes(h) && 'ring-1 ring-inset ring-foreground/40')} />
    ))}
  </div>
);

// "Recommend a grinding plan" from tomorrow's OTE prices (#74, concept).
export const EnergyPlanAdvisor = () => {
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);

  const recommend = async () => {
    setBusy(true);
    const res = await runAi('energy-plan', { lang: getLanguage(), prices: PRICES }, energyPlanDemo);
    setPlan({ ...res, eval: evaluatePlan(res.result.schedule, PRICES) });
    setBusy(false);
  };

  return (
    <Card className="p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <div className="text-sm font-semibold">{t('ai.energy.title')}</div>
        <HelpTip id="energy" />
        {plan && <AiBadge mode={plan.mode} className="ml-auto" />}
      </div>
      <div className="text-xs text-muted-foreground">{t('ai.energy.hint')}</div>
      {!plan ? (
        <Button size="sm" className="gap-1" onClick={recommend} disabled={busy}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {t('ai.energy.recommend')}
        </Button>
      ) : (
        <div className="space-y-3" aria-live="polite">
          <p className="text-sm">{plan.result.summary}</p>
          {MILLS.map(m => {
            const row = plan.eval.rows.find(r => r.millId === m.id);
            return (
              <div key={m.id} className="space-y-1">
                <div className="flex justify-between text-xs"><span className="font-medium">{m.name}</span><span className="tabular-nums">{czk(row.cost)}</span></div>
                <DayStrip hours={plan.result.schedule.find(s => s.millId === m.id).hours} habit={baselineHours(m)} />
              </div>
            );
          })}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md border p-2"><div className="text-[11px] text-muted-foreground">{t('ai.energy.habit')}</div><div className="font-semibold tabular-nums">{czk(plan.eval.baseline)}</div></div>
            <div className="rounded-md border p-2"><div className="text-[11px] text-muted-foreground">{t('ai.energy.plan')}</div><div className="font-semibold tabular-nums">{czk(plan.eval.cost)}</div></div>
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2"><div className="text-[11px] text-emerald-800">{t('ai.energy.saving')}</div><div className="font-bold tabular-nums text-emerald-800">{czk(plan.eval.saving)}</div></div>
          </div>
          <ul className="list-disc pl-5 text-xs text-muted-foreground">{plan.result.notes.map(n => <li key={n}>{n}</li>)}</ul>
          <div className="text-[11px] text-muted-foreground">{t('ai.energy.legend')}</div>
        </div>
      )}
    </Card>
  );
};
